import {
    useState,
    useRef,
    createRef,
    useEffect,
} from 'react'

import {
    useQuery,
    useMutation,
} from '@tanstack/react-query'

import {
    useLocation,
    useNavigate,
} from 'react-router-dom'

import PublicHeader from '../../publicHeader'
import BackgroundCheckStepper from '../BackgroundCheckForm/Components/BackgroundCheckStepper'
import axiosPublicClient from '../../axiosPublicClient'

import preCheckFormQuery from './services/pre-check-form'
import { useVerificationPolling } from './hooks/useVerificationPolling'
import savePreCheckConsentProgressMutation from './services/save-pre-check-consent-progress'
import submitPreCheckConsentMutation from './services/submit-pre-check-consent'
import submitPreCheckFormMutation from './services/submit-pre-check-form'
import trackPreCheckFormProgress from './services/track-pre-check-form-progress'
import { classifyFormError, classifyNoFormMessage } from './formAvailability'

import Overlay from '../../Components/Overlay'
import FormUnavailable from './Components/FormUnavailable'

import WelcomeAndStatusSelection from './Components/WelcomeAndStatusSelection'
import LegalDisclosuresAndConsentHub from './Components/LegalDisclosuresAndConsentHub'
import SignatureAndIdentifier from './Components/SignatureAndIdentifier'
import PreCheck from './Components/PreCheck'
import AdditionalDocument from './Components/AdditionalDocument'
import RetryDialog from './Components/RetryDialog'
import VerificationOverlay from './Components/VerificationOverlay'

const PreCheckForm = () => {

    const location = useLocation()

    const navigate = useNavigate()

    const refToPrint = useRef()

    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key')

    // Set the moment any candidate-endpoint call reports the link is no longer usable
    // (re-sent/rotated, expired, canceled, already submitted). Once set, the whole form
    // is replaced by a single clear message. Holds the reason string from the classifier.
    const [formUnavailable, setFormUnavailable] = useState(null)

    // A response interceptor scoped to this page catches the mid-form case: if the
    // candidate is partway through when the link stops working (e.g. the client re-sent
    // it, rotating the token), the next save/submit/upload — including ones fired by
    // child pages — fails, and instead of a silent no-op or a raw error the form flips
    // to the unavailable message. Registered on mount and ejected on unmount so it never
    // affects other pages that share axiosPublicClient. The error is still re-thrown so
    // existing per-call handling is untouched.
    useEffect(() => {
        const interceptorId = axiosPublicClient.interceptors.response.use(
            (response) => response,
            (interceptError) => {
                const reason = classifyFormError(interceptError)
                if (reason) {
                    setFormUnavailable(reason)
                }
                return Promise.reject(interceptError)
            },
        )

        return () => {
            axiosPublicClient.interceptors.response.eject(interceptorId)
        }
    }, [])

    const {
        data,
        error,
        isLoading,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['pre-check-form', key],
        queryFn: () => preCheckFormQuery(key),
        refetchOnWindowFocus: false,
        // Don't retry a dead link (rotated/expired/canceled) — the answer won't change,
        // and retrying just delays the message. Transient failures still get a couple of
        // attempts.
        retry: (failureCount, queryError) => !classifyFormError(queryError) && failureCount < 2,
    })

    const dataRef = useRef(data)
    dataRef.current = data

    const formUpdateIdRef = useRef(data?.formUpdateId)
    formUpdateIdRef.current = data?.formUpdateId

    const {
        error: savePreCheckConsentProgressError,
        isPending: savePreCheckConsentProgressIsPending,
        mutate: savePreCheckConsentProgress,
    } = useMutation({
        mutationFn: savePreCheckConsentProgressMutation,
        onSuccess: async (d) => {

            if (
                currentPage.type === "additional_document"
            ) {
                preCheckOnSubmitted()
                return
            }

            const nextPageIndex = currentPageIndex + 1

            if (currentPageIndex === signatureAndIdentifierPageIndex) {
                await submitPreCheckConsentAsync({
                    key,
                    currentPageType: currentPage.type,
                    currentPageDocumentId: currentPage.documentId,
                })
                const { data: freshData } = await refetch()
                if (freshData) {
                    dataRef.current = freshData
                }
            }

            // continue next page
            setCurrentPageIndex(nextPageIndex)
            setCurrentStep(nextPageIndex)
        }
    })

    const {
        error: mutationSubmitError,
        isPending: mutationSubmitIsPending,
        mutate: submitPreCheckForm,
        data: mutationSubmitData,
    } = useMutation({
        mutationFn: submitPreCheckFormMutation,
        onSuccess: (d) => {

            if (d?.unsuccessfulMessage) {
                navigate('/pre-check-form-failed', {
                    state: { message: d.unsuccessfulMessage },
                })
                return
            }

            navigate('/pre-check-form-success')
        }
    })

    const {
        isPending: submitPreCheckConsentIsPending,
        mutateAsync: submitPreCheckConsentAsync,
    } = useMutation({
        mutationFn: submitPreCheckConsentMutation,
    })

    const [currentPageIndex, setCurrentPageIndex] = useState(0)
    const [currentStep, setCurrentStep] = useState(0)
    const [showRetryDialog, setShowRetryDialog] = useState(false)

    const pageRefs = useRef([])

    const saveAdditionalPageRef = useRef(true)

    const {
        isFetching: formUpdateIdIsFetching,
        progressStatus,
        start: startPolling,
    } = useVerificationPolling({
        key,
        formUpdateIdRef,
        onSuccess: async () => {
            const { data: freshData } = await refetch()
            dataRef.current = freshData
            handleNext()
        },
        onFailure: () => setShowRetryDialog(true),
    })

    // Track how far the candidate gets through the form so an abandoned form's last
    // stage is known (powers the TruCheck "Last visited" filter). Fires whenever the
    // candidate lands on a page — including the first — and is best-effort, so it never
    // blocks or disrupts the form.
    useEffect(() => {
        const page = data?.Pages?.[currentPageIndex]
        if (!key || !page) {
            return
        }
        trackPreCheckFormProgress({
            key,
            currentPageType: page.type,
            currentPageDocumentId: page.documentId,
        })
    }, [key, currentPageIndex, data?.Pages])

    // Each subpage is a fresh step: when the candidate advances (or goes back), reset the
    // window scroll to the top so the new page starts from its beginning instead of keeping
    // the scroll position from where they finished the previous one.
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }, [currentPageIndex])

    // A mid-form call reported the link is no longer usable — replace everything with a
    // clear message. Checked before the loading/error branches so it wins even while a
    // refetch is in flight.
    if (formUnavailable) {
        return <FormUnavailable reason={formUnavailable} company={data?.company} />
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen my-4">
                <span className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
            </div>
        )
    }

    // Opening a dead link (rotated/expired/canceled/invalid) — show the matching
    // message; fall back to the generic one for any other load failure rather than
    // surfacing a raw axios error.
    if (error) {
        return <FormUnavailable reason={classifyFormError(error) || "generic"} />
    }

    if (!data) {
        return <FormUnavailable reason="generic" />
    }

    // The GET endpoint reports a spent/expired/canceled link as a 200 carrying
    // noFormMessage (and, for expired, the company's contact details).
    const noFormReason = classifyNoFormMessage(data)
    if (noFormReason) {
        return <FormUnavailable reason={noFormReason} company={data.company} />
    }

    const currentPage = data.Pages[currentPageIndex]

    let currentRef = pageRefs.current[currentPageIndex]

    if (!currentRef) {
        currentRef = createRef()
        pageRefs.current[currentPageIndex] = currentRef
    }

    const steps = data.Pages.map(p => p.title)

    const handlePrev = () => {
        const prevPageIndex = currentPageIndex - 1
        if (prevPageIndex < 0) {
            return
        }
        setCurrentPageIndex(prevPageIndex)

        setCurrentStep(prevPageIndex)

    }

    let nextButtonText = (
        currentPageIndex === data.Pages.length - 1 &&
        currentPage.type !== 'pre_check'
    ) ? "Submit" : "Next"

    const signatureAndIdentifierPageIndex = data.Pages.findIndex(page => page.type === "signature_and_identifier")

    const handleNext = async () => {

        const nextPageIndex = currentPageIndex + 1
        const currentPageData = currentRef.current.validateAndGetData()
        if (!currentPageData.isValid) {
            return
        }

        const activeData = dataRef.current
        const activePage = activeData.Pages[currentPageIndex]
        activePage.data = currentPageData.value

        const dataToSubmit = activeData.Pages.map(page => {
            return {
                type: page.type,
                data: page.data,
                veriffIntegration: page.veriffIntegration,
                documentId: page.documentId,
                analyzeId: page.analyzeId,

            }
        })

        const payload = {
            key,
            data: dataToSubmit,
            currentPageType: currentPage.type,
            currentPageDocumentId: currentPage.documentId,
        }

        // An additional_document page saves the upload and then polls for the async
        // analysis to finish (signalled by a formUpdateId change). Once the document is
        // already analyzed (analyzeId set) there is nothing left to wait for — most
        // importantly when the candidate returns after closing the browser once analysis
        // had completed: the formUpdateId was already bumped, so the poll would compare
        // an unchanged value forever and get stuck. Skip the save/poll in that case and
        // just advance (the document + its analysis are already persisted server-side).
        if (
            currentPage.type === "additional_document" &&
            !currentPage.analyzeId
        ) {
            if (saveAdditionalPageRef.current) {
                saveAdditionalPageRef.current = false
                savePreCheckConsentProgress(payload)
                return
            }
            else {
                saveAdditionalPageRef.current = true
            }

        }

        if (refToPrint.current) {
            savePreCheckConsentProgress(payload)
            return
        }

        const activePreCheckPageIndex = activeData.Pages.findIndex(page => page.type === "pre_check")

        if (nextPageIndex === activePreCheckPageIndex) {
            savePreCheckConsentProgress(payload)
            return
        }

        if (nextPageIndex >= activeData.Pages.length) {

            submitPreCheckForm(payload)
            return
        }

        // continue next page
        setCurrentPageIndex(nextPageIndex)
        setCurrentStep(nextPageIndex)

    }

    const preCheckOnSubmitted = () => startPolling()

    // Called after a pre_check page is skipped (skip already recorded on the
    // backend by PreCheck). Deliberately runs only the "advance or submit"
    // branch of handleNext — no validation, consent upload, or progress-save
    // mutations.
    const preCheckOnSkipped = () => {
        const nextPageIndex = currentPageIndex + 1
        const activeData = dataRef.current

        if (nextPageIndex >= activeData.Pages.length) {
            const dataToSubmit = activeData.Pages.map(page => ({
                type: page.type,
                data: page.data,
                veriffIntegration: page.veriffIntegration,
                documentId: page.documentId,
                analyzeId: page.analyzeId,
            }))

            const payload = {
                key,
                data: dataToSubmit,
                currentPageType: currentPage.type,
                currentPageDocumentId: currentPage.documentId,
            }

            submitPreCheckForm(payload)
            return
        }

        setCurrentPageIndex(nextPageIndex)
        setCurrentStep(nextPageIndex)
    }


    return (
        <div className="h-screen flex flex-col bg-gray-50">

            <PublicHeader referenceNumber={data.referenceNumber} />

            <BackgroundCheckStepper steps={steps} currentStep={currentStep} />

            <div className="flex flex-col flex-grow items-center justify-center">
                {
                    currentPage.type === "welcome_and_status_selection" &&
                    <WelcomeAndStatusSelection
                        ref={currentRef}
                        data={currentPage.data}
                        handleNext={handleNext}
                        refToPrint={refToPrint}
                    />
                }
                {
                    currentPage.type === "legal_disclosures_and_consent_hub" &&
                    <LegalDisclosuresAndConsentHub
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                        company={currentPage.company}
                        handleNext={handleNext}
                    />
                }
                {
                    currentPage.type === "signature_and_identifier" &&
                    <SignatureAndIdentifier
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                        company={currentPage.company}
                        formKey={key}
                        handleNext={handleNext}
                        refetch={refetch}
                    />
                }
                {
                    currentPage.type === "pre_check" &&
                    <PreCheck
                        key={currentPage.documentId}
                        ref={currentRef}
                        data={currentPage.data}
                        company={currentPage.company}
                        onSubmitted={() => preCheckOnSubmitted()}
                        onSkipped={() => preCheckOnSkipped()}
                        handleNext={handleNext}
                        documentText={currentPage.title}
                        documentId={currentPage.documentId}
                        analyzeId={currentPage.analyzeId}
                        optional={currentPage.optional}
                        skipLabel={currentPage.skipLabel || 'Do not have US ID'}
                        formKey={key}
                        refetch={refetch}
                        // The TruCheck Card photo is the selfie from the FIRST verification
                        // session, so the card-photo notice is shown only on that step.
                        isFirstVerification={currentPageIndex === data.Pages.findIndex(p => p.type === 'pre_check')}
                    />
                }

                {
                    (
                        currentPage.type === "additional_document"
                    ) &&
                    <AdditionalDocument
                        key={currentPage.documentId}
                        ref={currentRef}
                        data={currentPage.data}
                        requestKey={key}
                        title={currentPage.title}
                        description={currentPage.description}
                        documentId={currentPage.documentId}
                        documentOption={currentPage.documentOption}
                        analyzeId={currentPage.analyzeId}
                    />
                }

            </div>



            {(false) && (
                <p className="mt-1 text-base text-red-600 py-4 mx-auto">
                    Error
                </p>
            )}

            <div className="flex justify-end gap-6 p-4 border-t">
                <button
                    onClick={handlePrev}
                    disabled={currentPageIndex === 0}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                    Prev
                </button>
                <button
                    onClick={handleNext}
                    className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed ${currentPage.type === 'pre_check' ? 'invisible pointer-events-none' : ''}`}
                >
                    {nextButtonText}
                </button>
            </div>

            {/* Overlay */}
            {
                (
                    isFetching ||
                    savePreCheckConsentProgressIsPending ||
                    submitPreCheckConsentIsPending ||
                    mutationSubmitIsPending
                ) &&
                <Overlay />
            }

            {
                formUpdateIdIsFetching &&
                <VerificationOverlay
                    progressStatus={progressStatus}
                />
            }

            {
                showRetryDialog &&
                <RetryDialog
                    text="Results could not be retrieved yet. Please try again."
                    submitText="Retry"
                    onSubmit={() => {
                        setShowRetryDialog(false)
                        preCheckOnSubmitted()
                    }}
                />
            }

        </div>

    )
}

export default PreCheckForm
